import { gql } from 'graphql-request';
import { For, Match, Show, Switch, createEffect, createSignal, on, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { Button } from '../../../../ui-components/button/button';
import { Collection, pdfStore, setPdfStore } from '../../pdf-store';
import { incrementUserPdfPrintCount } from '../pdf-newsletter-signup-prompt/newsletter-prompt-checker';
import removeAmpersand from '../../../../tools/remove-ampersand';
import { DeleteIcon } from '../../../icons-library/delete';
import { ChevronDownCviIcon } from '../../../icons-library/chevron-down-cvi';
import { FileDownloadIcon } from '../../../icons-library/file-download';
import { StatusNote } from '../status-note/status-note';

const GET_PDF_QUERY = gql`
    query GeneratePdfFile (
        $domain: String!,
        $environment: String!,
        $postKeys: [String],
    ) {
        resource: generatePdfFile(
            domain: $domain, 
            environment: $environment,
            postKeys: $postKeys,
        ) {
            buffer, 
            success,
            message,
            maxConcurrent,
        }
    }
`;

const maxRetryAttempts = 2;

type CollectionSingleProps = {
    collection: Collection;
    deleteCollection: (collectionId: string) => void;
    editCollection: () => void;
    reset: () => void;
    isExpanded: boolean;
    expandTimestamp?: number;
};

export const CollectionSingle = (props: CollectionSingleProps) => {
    const {
        graphQlClient,
        deploymentEnvironment,
        localize,
    } = useContext(AppContext);

    const [ isDeleting, setIsDeleting ] = createSignal(false);
    const [ generationRetryCount, setGenerationRetryCount ] = createSignal(0);

    /* Native <details> owns the open state so the user can toggle it before hydration.
       "Expand all" / "Collapse all" and the editing rules below write to the element
       directly — a signal would skip the write when its value hasn't changed, and the
       user may have toggled the panel in between. */
    let details: HTMLDetailsElement | undefined;
    const setOpen = (open: boolean) => {
        if (details) details.open = open;
    };

    const isActiveCollection = () => pdfStore.activeCollectionId === props.collection.id;

    const busy = () => {
        // Disables interactivity while the PDF file generator is busy
        return (
            pdfStore.status === 'generating'
            || pdfStore.status === 'retrying-generating'
            || pdfStore.status === 'checking-availability'
            || pdfStore.status === 'creating'
            || pdfStore.status === 'deleting'
            || pdfStore.status === 'updating-existing'
            || pdfStore.status === 'editing'
            || pdfStore.activeCollectionId !== ''
        );
    };

    createEffect(
        on(
            // The timestamp is used to force re-render when expanding/collapsing
            [() => props.isExpanded, () => props.expandTimestamp],
            () => setOpen(props.isExpanded),
            { defer: true }
        ),
    );

    createEffect(
        on(
            [() => pdfStore.status, () => pdfStore.activeCollectionId],
            () => {
                if (pdfStore.status === 'editing' && pdfStore.activeCollectionId) {
                    // When editing, collapse all other collections
                    if (pdfStore.activeCollectionId !== props.collection.id) {
                        setOpen(false);
                    }
                }
            }
        )
    );

    const requestFile = async (collection: Collection, retryCount: number = 0) => {
        try {
            setPdfStore('activeCollectionId', collection.id);
            setPdfStore('collections', col => col.title === collection.title, 'downloaded', false);
            setPdfStore('status', 'generating');
            setGenerationRetryCount(retryCount);
            setPdfStore('output', {
                id: collection.id,
                blob: ''
            });
            
            const postKeys = collection.orderedPosts.map((item) => item.key);
            const file = await fetchFile(postKeys, collection);

            if (!file) {
                return;
            }
            
            setPdfStore('output', 'blob', file);
            setPdfStore('collections', col => col.title === collection.title, 'downloaded', true);

            props.reset();

            triggerDownload(collection.title, file);
        } catch (err: any) {
            console.log('PDF file request error: ', err);
            setPdfStore('status', 'error');
            return;
        }
    };

    const fetchFile = async (postKeys: string[], collection: Collection) => {
        try {
            const variables = {
                domain: collection.siteInfo.domain,
                environment: deploymentEnvironment,
                postKeys,
            };
    
            const response = await graphQlClient(GET_PDF_QUERY, variables, false);
    
            if (!response || !response.resource) {
                throw new Error('No response from server');
            }
    
            const retryAttemptsReached = generationRetryCount() >= maxRetryAttempts;
            
            if (response.resource.maxConcurrent && !retryAttemptsReached) {
                // Retry download twice if the PDF service is busy
                setPdfStore('status', 'retrying-generating');
                const count = generationRetryCount() + 1;
                setGenerationRetryCount(count);
    
                setTimeout(() => {
                    requestFile(collection, count);
                }, 8000);
    
                return;
            }            
    
            if (response.resource.maxConcurrent && retryAttemptsReached) {
                setPdfStore('status', 'server-busy');
                return;
            }
    
            if (!response.resource.success) {                
                throw new Error(response.resource.message);
            }
    
            return response.resource.buffer;
        } catch (err: any) {
            console.log('PDF fetch file error: ', err);
            setPdfStore('status', 'error');
            return;
        }
    };

    const triggerDownload = (title: string, blob: string) => {
        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${blob}`;
        link.download = `${title}.pdf`;
        link.click();
    };

    /* Post titles dim on the other cards while one collection is generating, so the
       active card reads as the one doing the work. */
    const dimmed = () => busy() && !isActiveCollection();

    const PostsList = () => (
        <ul class={clsx(
            'm-0 mb-cvi-6 list-disc pl-cvi-6 body-sm font-cvi-regular marker:text-cvi-fg-muted',
            dimmed() ? 'text-cvi-fg-subtle' : 'text-cvi-fg-default',
        )}>
            <For each={props.collection.orderedPosts}>{(item) => (
                <li class="py-cvi-1 break-words">{removeAmpersand(item.postTitle)}</li>
            )}</For>
        </ul>
    );

    const DeleteButtons = () => (
        <Show
            when={isDeleting()}
            fallback={
                <button
                    type="button"
                    class="icon-button-ghost icon-button-ghost-sm"
                    onClick={() => setIsDeleting(true)}
                    disabled={busy()}
                    aria-label={localize('pdf-delete-collection', 'Delete')}
                >
                    <DeleteIcon size="small" stroke="currentColor" fill="none" ariaHidden={true} />
                </button>
            }
        >
            <Button
                label={localize('pdf-delete-collection', 'Delete')}
                variant='ghost'
                small={true}
                noBlockSpacing
                onClick={() => {
                    props.deleteCollection(props.collection.id);
                    setIsDeleting(false);
                }}
                disabled={busy()}
            />
            <Button
                label={localize('pdf-cancel-delete', 'Cancel')}
                variant='ghost'
                small={true}
                noBlockSpacing
                onClick={() => setIsDeleting(false)}
                disabled={busy()}
            />
        </Show>
    );

    const EditButton = () => (
        <Button
            label={localize('pdf-collection-edit', 'Edit')}
            disabled={busy()}
            variant='secondary-neutral'
            small={true}
            noBlockSpacing
            onClick={() => {
                props.editCollection();
            }}
        />
    );

    const DownloadButton = () => (
        <Button
            label={props.collection.downloaded
                ? localize('pdf-download-again', 'Download again')
                : localize('pdf-download', 'Download')
            }
            disabled={busy()}
            variant='primary'
            small={true}
            noBlockSpacing
            iconEnd={<FileDownloadIcon size="small" fill="currentColor" ariaHidden={true} />}
            onClick={() => {
                incrementUserPdfPrintCount();
                requestFile(props.collection);
            }}
        />
    );

    /* One status surface per card; only the active collection shows one. */
    const StatusMessages = () => (
        <Show when={isActiveCollection()}>
            <Switch>
                <Match when={pdfStore.status === 'generating' && !pdfStore.output.blob}>
                    <StatusNote loading class="mt-cvi-6">
                        {localize('pdf-generating-file', 'Generating file, download will occur once complete')}
                    </StatusNote>
                </Match>
                <Match when={pdfStore.status === 'updating-existing'}>
                    <StatusNote loading class="mt-cvi-6">
                        {localize('pdf-saving-collection-status', 'Saving, please wait...')}
                    </StatusNote>
                </Match>
                <Match when={pdfStore.status === 'deleting'}>
                    <StatusNote loading class="mt-cvi-6">
                        {localize('pdf-deleting-collection-status', 'Deleting collection, please wait...')}
                    </StatusNote>
                </Match>
                <Match when={pdfStore.status === 'editing'}>
                    <StatusNote tone="highlight" class="mt-cvi-6">
                        <div class="flex flex-wrap items-center justify-between gap-cvi-4">
                            <span>{localize('pdf-editing-collection-status', 'Editing collection')}</span>
                            <Button
                                label={localize('pdf-cancel-edit', 'Cancel edit')}
                                variant='ghost'
                                small={true}
                                noBlockSpacing
                                onClick={() => props.reset()}
                            />
                        </div>
                    </StatusNote>
                </Match>
                <Match when={pdfStore.status === 'retrying-generating'}>
                    <StatusNote loading class="mt-cvi-6">
                        {`${localize('pdf-file-generator-busy-retrying', 'File generator busy, retrying in a few seconds')} ${localize('pdf-file-generator-attempt', 'Attempt')} ${generationRetryCount()} / ${maxRetryAttempts}`}
                    </StatusNote>
                </Match>
                <Match when={pdfStore.status === 'server-busy'}>
                    <StatusNote tone="highlight" class="mt-cvi-6">
                        {localize('pdf-file-generator-at-capacity', 'File generator at capacity, please try again later')}
                    </StatusNote>
                </Match>
                <Match when={pdfStore.status === 'error'}>
                    <StatusNote tone="danger" class="mt-cvi-6">
                        {localize('pdf-download-error', 'An error has occurred, please try again later or contact support if the problem persists.')}
                    </StatusNote>
                </Match>
            </Switch>
        </Show>
    );

    /* DS Accordion item (Storybook `components-accordion-accordion-item`) on a DS card
       surface. The active collection lifts to the raised elevation while it works. */
    return (
        <details
            ref={details}
            class={clsx(
                'group overflow-hidden rounded-cvi-md border border-cvi-border-muted transition-shadow duration-200 ease-cvi-standard',
                dimmed() ? 'bg-cvi-bg-block-neutral' : 'bg-cvi-bg-elevation-raised',
                isActiveCollection() && 'shadow-elevation-raised',
            )}
        >
            <summary class="focus-ring-cvi-flush flex cursor-pointer list-none items-center gap-cvi-5 px-cvi-6 py-cvi-5 [&::-webkit-details-marker]:hidden">
                <h3 class={clsx(
                    'heading-md font-cvi-medium m-0 min-w-0 flex-1 break-words',
                    dimmed() ? 'text-cvi-fg-subtle' : 'text-cvi-fg-default',
                )}>
                    {removeAmpersand(props.collection.title)}
                </h3>
                <span class="inline-flex size-cvi-icon-box shrink-0 items-center justify-center text-cvi-fg-default transition-transform duration-300 motion-reduce:transition-none group-open:rotate-180">
                    <ChevronDownCviIcon size="lg" ariaHidden={true} />
                </span>
            </summary>

            <div class="px-cvi-6 pb-cvi-6">
                <PostsList />

                <div class="flex flex-wrap items-center justify-end gap-cvi-4">
                    <DeleteButtons />
                    <EditButton />
                    <DownloadButton />
                </div>

                <StatusMessages />
            </div>
        </details>
    );
};
